import { observer } from 'mobx-react-lite'
import { type ITabItem, Tabs, Icon, Typography, Divider } from 'shared/ui'
import { numberFormat } from 'shared/lib'
import {
  type ISelectedPlanInfo,
  type BillingPeriod,
} from 'pages/settings/ui/BillingPlan/store/plansSelectorStore'
import styles from './styles.module.scss'

type ITimePeriodInfoProps = {
  period: BillingPeriod
  handleSetPeriod: (period: BillingPeriod) => void
  isPlanLoading: boolean
  annualSavings: number
  shouldShowPeriodTabs: boolean
  selectedPlanInfo: ISelectedPlanInfo | null
}

export const TimePeriodInfo = observer(
  ({
    period,
    handleSetPeriod,
    isPlanLoading,
    annualSavings,
    shouldShowPeriodTabs,
    selectedPlanInfo,
  }: ITimePeriodInfoProps) => {
    const handlePeriodChange = (tab: ITabItem<BillingPeriod>) => {
      handleSetPeriod(tab.key)
    }

    const periodTabs: ITabItem<BillingPeriod>[] = [
      {
        name: 'Monthly',
        key: 'monthly',
        disabled: isPlanLoading,
      },
      {
        name: 'Annually',
        key: 'annually',
        disabled: isPlanLoading,
        statusProps: {
          title: `Save ${numberFormat({
            value: annualSavings,
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`,
          emphasis: 'high',
          intent: 'positive',
          size: 'small',
        },
      },
    ]

    const planInfo = selectedPlanInfo

    return (
      <div className={styles.timePeriodInfo}>
        {shouldShowPeriodTabs && (
          <>
            <Tabs
              disabledHover={isPlanLoading}
              variant='simpleWithoutBorder'
              tabs={periodTabs}
              activeTabKey={period}
              handleSelectTab={handlePeriodChange}
              tabsClassName={styles.tabs}
            />
            <Divider
              style={{
                margin: '16px 0 24px',
                borderColor: 'var(--black-20)',
              }}
            />
          </>
        )}

        {planInfo && (
          <div className={styles.planInfo}>
            <div className={styles.planHeader}>
              <Icon icon={planInfo.icon} fontSize={24} color={planInfo.iconColor} />
              <Typography ariaLabel='plan_name' variant='body-lg-medium'>
                {planInfo.name}
              </Typography>
            </div>

            <div className={styles.planDetails}>
              <Typography ariaLabel='plan_credits' variant='body-md-regular'>
                {numberFormat({ value: planInfo.credits })} credits /
                <span className={styles.periodSuffix}>
                  {period === 'annually' ? 'year' : 'month'}
                </span>
              </Typography>
              <Typography ariaLabel='additional_credit_price' variant='body-md-regular'>
                {numberFormat({
                  value: planInfo.additionalCreditPrice,
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                })}
                &nbsp;per additional credit
              </Typography>
            </div>
          </div>
        )}
      </div>
    )
  }
)
